import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  SignalButton,
  SignalDropdown,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { ContactResponse } from '@core/api/workspace/models/contactResponse';
import { ContactResponseStatus } from '@core/api/workspace/models/contactResponseStatus';
import type { PatchContactApiResponse } from '@core/api/workspace/models/patchContactApiResponse';
import { PatchContactRequestStatus } from '@core/api/workspace/models/patchContactRequestStatus';

export interface EditContactOverlayData {
  id: string;
  firstName: string;
  lastName?: string;
  email: string;
  status: ContactResponseStatus;
}

export type EditContactOverlayResult = ContactResponse;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATUS_OPTIONS: {
  label: string;
  value: PatchContactRequestStatus;
}[] = [
  { label: 'Active', value: PatchContactRequestStatus.ACTIVE },
  { label: 'Inactive', value: PatchContactRequestStatus.INACTIVE },
  { label: 'Suspended', value: PatchContactRequestStatus.SUSPENDED },
];

@Component({
  selector: 'app-edit-contact-overlay',
  standalone: true,
  imports: [SignalButton, SignalDropdown, SignalTextField],
  templateUrl: './edit-contact-overlay.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditContactOverlayComponent extends BaseComponent {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly dialogRef = inject(
    DialogRef<EditContactOverlayResult | null>,
  );
  private readonly data = inject<EditContactOverlayData>(DIALOG_DATA);

  readonly patchState = new State<PatchContactApiResponse>();
  readonly firstName = signal(this.data.firstName);
  readonly lastName = signal(this.data.lastName ?? '');
  readonly email = signal(this.data.email);
  readonly status = signal<PatchContactRequestStatus>(
    this.data.status as PatchContactRequestStatus,
  );
  readonly statusOptions = STATUS_OPTIONS;

  close(): void {
    this.dialogRef.close(null);
  }

  onStatusChange(value: unknown): void {
    this.status.set(value as PatchContactRequestStatus);
  }

  onSave(): void {
    const firstName = this.firstName().trim();
    const lastName = this.lastName().trim();
    const email = this.email().trim().toLowerCase();

    if (!firstName) {
      this.toasterService.error('Enter a first name.');
      return;
    }
    if (!email || !EMAIL_PATTERN.test(email)) {
      this.toasterService.error('Enter a valid email address.');
      return;
    }

    this.executeRequest({
      state: this.patchState,
      request: this.userAPIService.patchContact(this.data.id, {
        firstName,
        lastName,
        email,
        status: this.status(),
      }),
      showLoader: true,
      handleError: true,
      onSuccess: (response) => {
        this.toasterService.success('Contact updated.');
        this.dialogRef.close(response.data);
      },
    });
  }
}
